import React from 'react';
import { Check, Copy } from 'lucide-react';
import i18n from '../i18n';
import { p } from '../i18n/useP';
import { errorReport } from './Toast';
import './ErrorBoundary.css';

/**
 * Filet de sécurité du rendu.
 *
 * Sans lui, une erreur dans n'importe quel écran fait démonter TOUT l'arbre par
 * React : l'utilisateur se retrouve devant une page vide, sans un mot, et rien
 * ne dit s'il faut recharger, changer de client ou appeler à l'aide.
 *
 * Ici on attrape l'erreur, on garde la navigation debout et on affiche une
 * sortie : revenir au Cockpit, ou recharger. Le message technique est là, mais
 * replié — il sert au signalement, pas à la lecture.
 *
 * Remonté avec une `key` liée à la page : changer d'écran remet le filet à
 * zéro, sinon une erreur ponctuelle condamnerait la session entière.
 *
 * « Copier » (2026-10-09), comme sur le message d'erreur : le message, l'écran,
 * le client, l'exercice, la date, puis la pile, d'un seul geste à coller dans
 * un signalement. `context` est celui du message d'erreur (App).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: '', at: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Pas de service de collecte à ce jour : la console reste la trace.
    // eslint-disable-next-line no-console
    console.error('[Palestre] écran en erreur', error, info?.componentStack);
    this.setState({ componentStack: info?.componentStack || '', at: Date.now() });
  }

  report() {
    const { error, componentStack, at } = this.state;
    const head = errorReport({ msg: String(error?.message || error), context: this.props.context, at },
      (k) => i18n.t(k));
    const lines = (txt) => String(txt || '').split('\n').map((x) => x.trim()).filter(Boolean).slice(0, 10).join('\n');
    return [head, lines(error?.stack), lines(componentStack)].filter(Boolean).join('\n\n');
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const back = () => {
      this.setState({ error: null, copied: false });
      if (this.props.onReset) this.props.onReset();
    };

    return (
      <div className="eb">
        <div className="eb__card">
          <div className="eb__label">{p('Écran interrompu')}</div>
          <h2 className="eb__title">{p('Cet écran n’a pas pu s’afficher.')}</h2>
          <p className="eb__text">
            {p('Le reste de l’application fonctionne. Aucune donnée n’a été modifiée : cette page n’a fait que lire.')}
          </p>
          <div className="eb__actions">
            <button type="button" className="eb__btn eb__btn--main" onClick={back}>
              {p('Retour au cockpit')}
            </button>
            <button type="button" className="eb__btn" onClick={() => window.location.reload()}>
              {p('Recharger la page')}
            </button>
          </div>
          <details className="eb__det">
            <summary>{p('Détail technique')}</summary>
            <pre>{String(error?.message || error)}</pre>
            <button type="button" className="eb__copy"
                    onClick={() => Promise.resolve(navigator.clipboard?.writeText(this.report()))
                      .then(() => this.setState({ copied: true })).catch(() => {})}>
              {this.state.copied ? i18n.t('common:toast.copied') : i18n.t('common:toast.copy')}
              {this.state.copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
            </button>
          </details>
        </div>
      </div>
    );
  }
}
